import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getLocales } from 'expo-localization';
import AsyncStorage from '@react-native-async-storage/async-storage';

import en from './locales/en.json';
import es from './locales/es.json';

const resources = {
  en: { translation: en },
  es: { translation: es },
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
] as const;

export type LanguageCode = 'en' | 'es';

const LANGUAGE_STORAGE_KEY = '@hyzone_language';

// Get device language safely
export const getDeviceLanguage = (): LanguageCode => {
  try {
    const locales = getLocales();
    if (locales.length > 0) {
      const languageCode = locales[0].languageCode;
      // Check if we support this language
      if (languageCode && Object.keys(resources).includes(languageCode)) {
        return languageCode as LanguageCode;
      }
    }
  } catch (error) {
    // Fallback for web or if getLocales fails
    console.warn('Failed to get device locale:', error);
  }
  return 'en'; // Default to English
};

// Load saved language preference
export const loadSavedLanguage = async (): Promise<LanguageCode | null> => {
  try {
    const savedLang = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLang && Object.keys(resources).includes(savedLang)) {
      return savedLang as LanguageCode;
    }
  } catch (error) {
    console.error('Failed to load language preference:', error);
  }
  return null;
};

// Save language preference
export const saveLanguage = async (lang: LanguageCode): Promise<void> => {
  try {
    await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch (error) {
    console.error('Failed to save language preference:', error);
  }
};

// Change language
export const changeLanguage = async (lang: LanguageCode): Promise<void> => {
  await i18n.changeLanguage(lang);
  await saveLanguage(lang);
};

// Initialize with saved or device language
const initLanguage = async (): Promise<LanguageCode> => {
  const savedLang = await loadSavedLanguage();
  return savedLang || getDeviceLanguage();
};

i18n.use(initReactI18next).init({
  resources,
  lng: getDeviceLanguage(), // Initial fallback, will be updated by initLanguage
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  compatibilityJSON: 'v4',
});

// Load saved language preference on startup
initLanguage().then((lang) => {
  if (lang !== i18n.language) {
    i18n.changeLanguage(lang);
  }
});

export default i18n;
